import { Components, Theme } from '@mui/material/styles';
import { flowStepIconClasses } from './StepLabelIcon';

export const flowStepper: Components<Theme>['MuiStepper'] = {
  defaultProps: {
    alternativeLabel: true,
    size: 'medium',
  },
  styleOverrides: {
    horizontal: {
      '& .MuiStepContent-root': {
        borderLeft: 'none',
      },
      '&:not(.MuiStepper-alternativeLabel)': {
        alignItems: 'flex-start',
        '& .MuiStepConnector-root': {
          marginTop: '13px',
        },
      },
    },
    vertical: ({ theme, size }) => ({
      '& .MuiStep-alternativeLabel': {
        // when vertical and alternativeLabel don't display connector
        '& .MuiStepConnector-root': {
          display: 'none',
        },
        // when vertical and alternativeLabel the content should be right on side
        '& .MuiStepContent-root': {
          paddingLeft: '0',
          marginLeft: '0',
          border: 'none',
        },
      },
      '& .MuiStepContent-root': {
        paddingLeft: '24px',
        marginLeft: '12px',
        // when the size is not small apply border
        ...(size !== 'small' && { borderLeft: `2px solid ${theme.palette.onSurface.os300}` }),
      },
      '& .MuiStepLabel-alternativeLabel': {
        alignItems: 'flex-start',
        textAlign: 'start',
      },
      ...(size === 'small' && {
        '& .MuiStepLabel-vertical': {
          padding: '0',
        },
        '& .MuiStepLabel-iconContainer.Mui-active > div': {
          width: '20px',
          height: '60px',
        },
      }),
    }),
    root: ({ size, theme }) => ({
      ...(size === 'small' && {
        // hide dividers
        '& .MuiStepConnector-root': {
          display: 'none',
        },
        // hide labels
        '& .MuiStepLabel-labelContainer': {
          display: 'none',
        },
        '& .MuiStep-root': {
          lineHeight: '0',
          flexGrow: '0',
          flexBasis: 'auto',
          flexShrink: '1',
          padding: '0',
          minWidth: '24px',
          transition: 'flex-basis 0.3s ease',
          '& *': {
            maxWidth: '100%',
          },
          '& .MuiStepButton-root': {
            boxSizing: 'border-box',
            // use of padding instead of gap to increase clickable area
            padding: theme.spacing(0.25),
            [theme.breakpoints.up('xl')]: {
              '&.MuiStepButton-horizontal': {
                paddingLeft: theme.spacing(0.5),
                paddingRight: theme.spacing(0.5),
              },
              '&.MuiStepButton-vertical': {
                paddingTop: theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
              },
            },
          },
          '&:first-of-type': {
            '& .MuiStepButton-horizontal': {
              paddingLeft: theme.spacing(0.25),
            },
            '& .MuiStepButton-vertical': {
              paddingTop: theme.spacing(0.25),
            },
          },
          '&:last-of-type': {
            '& .MuiStepButton-horizontal': {
              paddingRight: theme.spacing(0.25),
            },
            '& .MuiStepButton-vertical': {
              paddingBottom: theme.spacing(0.25),
            },
          },
        },
        '& .MuiStepLabel-iconContainer': {
          // in compact mode show icon only for error
          '&:not(.Mui-error) svg': {
            display: 'none',
          },
          '& > div': {
            minWidth: '20px',
            width: '20px',
            height: '20px',
            minHeight: '20px',
            fontSize: '14px',
            transition: 'width 0.3s ease, height 0.3s ease',
          },
          '&.Mui-active > div': {
            width: '200px',
          },
        },
        // when the stepper is in small variant, the inset of the icon should be 0
        [`&&& .${flowStepIconClasses.root}.${flowStepIconClasses.active}::before`]: {
          inset: '0',
          borderWidth: '0',
          animation: 'none',
        },
      }),
    }),
    nonLinear: ({ theme, size }) => ({
      // only non-linear variant show disabled style
      [`& .${flowStepIconClasses.root}.${flowStepIconClasses.disabled}`]: {
        // exclamation mark
        ...(size !== 'small' && {
          [`&:not(.${flowStepIconClasses.completed})::after`]: {
            content: '"!"',
            lineHeight: '24px',
            fontSize: 'inherit',
            fontWeight: '800',
            color: theme.palette.background.primary,
          },
        }),
        // default
        [`&:not(.${flowStepIconClasses.active})`]: {
          borderColor: theme.palette.disabled.secondary,
          backgroundColor: theme.palette.disabled.secondary,
        },
        // active
        [`&:.${flowStepIconClasses.active}`]: {
          borderColor: theme.palette.disabled.secondary,
          '&::before': {
            backgroundColor: theme.palette.disabled.secondary,
          },
        },
      },
    }),
  },
};
